/**
 * SettingsDirtyContext — cross-section save-bar orchestration.
 *
 * Each section registers `dirty`, `onSave`, `onDiscard`.  The layout renders a
 * single sticky bottom bar whenever any section reports pending edits.
 *
 * Reliability contract (Feb 2026 rewrite — root-caused two production bugs):
 *   1. **No stale closures.** `useDirtyForm` keeps `value`, `baseline`, `dirty`
 *      in a ref and re-registers with the shell on EVERY re-render — the shell
 *      never holds a save handler closed over an old value.
 *   2. **No silent data loss on error.** If the section's onSave throws, the
 *      shell keeps `dirty = true`, clears only `saving`, and re-throws so the
 *      section's toast surfaces the failure. The user's edits are preserved.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";

const Ctx = createContext(null);

export function SettingsDirtyProvider({ children }) {
  const [state, setState] = useState({ dirty: false, saving: false });
  const handlersRef = useRef({ save: null, discard: null });

  const register = useCallback(({ dirty, onSave, onDiscard }) => {
    handlersRef.current = { save: onSave, discard: onDiscard };
    setState((s) => (s.dirty === !!dirty ? s : { ...s, dirty: !!dirty }));
  }, []);

  const clear = useCallback(() => {
    handlersRef.current = { save: null, discard: null };
    setState({ dirty: false, saving: false });
  }, []);

  // Save — keeps `dirty` true on error so the sticky bar stays visible and the
  // user's edits are preserved.  Re-throws so the caller (or downstream toast)
  // can react to the failure explicitly.
  const save = useCallback(async () => {
    if (!handlersRef.current.save) return;
    setState((s) => ({ ...s, saving: true }));
    try {
      await handlersRef.current.save();
      setState({ dirty: false, saving: false });
    } catch (err) {
      // Preserve the dirty state — nothing was persisted.
      setState((s) => ({ ...s, saving: false }));
      throw err;
    }
  }, []);

  const discard = useCallback(() => {
    handlersRef.current.discard && handlersRef.current.discard();
    setState((s) => ({ ...s, dirty: false }));
  }, []);

  const value = useMemo(
    () => ({ ...state, register, clear, save, discard }),
    [state, register, clear, save, discard],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettingsDirty() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSettingsDirty must be inside SettingsDirtyProvider");
  return v;
}

/* ---------------------------------------------------------------------- */

/**
 * useDirtyForm — the ergonomic hook for section pages.
 *
 *   const { value, setValue, dirty, save } = useDirtyForm({
 *     initial: { ... },              // reset target — usually derived from fetched data
 *     onSave:  (v) => api.patch(...),// receives the LATEST value on save
 *     deps:    [clinic],             // when this changes we sync baseline to `initial`
 *   });
 *
 * Guarantees
 *   • `save` always receives the CURRENT value — no stale closures, ever.
 *   • `save` re-throws so the caller can toast + keep `dirty` true.
 *   • Baseline resets only when `deps` change (i.e. after a fresh server fetch).
 */
export function useDirtyForm({ initial, onSave, deps = [] }) {
  const [value,    setValue]    = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const shell = useSettingsDirty();

  // Reset baseline whenever the loaded `initial` changes (e.g. after re-fetch).
  useEffect(() => {
    setValue(initial);
    setBaseline(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const dirty = useMemo(
    () => JSON.stringify(value) !== JSON.stringify(baseline),
    [value, baseline],
  );

  // Latest-value ref — read inside save/discard so we never close over stale state.
  const latestRef = useRef({ value, baseline, dirty, onSave });
  latestRef.current = { value, baseline, dirty, onSave };

  // Stable save/discard handlers — safe to hand to the shell every render.
  const save = useCallback(async () => {
    const { dirty: d, value: v, onSave: fn } = latestRef.current;
    if (!d) return;
    await fn(v);
    // Only reached on success — bump baseline so the form becomes clean.
    setBaseline(v);
  }, []);

  const discard = useCallback(() => {
    setValue(latestRef.current.baseline);
  }, []);

  // Re-register with the shell on every render so the shell always has the
  // freshest `dirty` flag alongside our stable handlers. Intentional — a deps
  // array here would either (a) cause stale-closure bugs the exact hook is
  // designed to avoid, or (b) trigger re-renders on every value change.
  useEffect(() => {
    shell.register({ dirty, onSave: save, onDiscard: discard });
  });   // eslint-disable-line react-hooks/exhaustive-deps

  // On unmount only — clear the shell so the save bar disappears.
  useEffect(() => {
    return () => shell.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { value, setValue, baseline, dirty, save, discard };
}
